import { Button, Grid, Group, Radio, Select, Stack, Switch, Textarea, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useHotkeys } from '@mantine/hooks'
import type { User, UserSchema } from '@opsopti/shared'
import {
  castUserToForm,
  isFieldsListValid,
  useGlobalState,
  userSchema,
  useUserCreateMutation,
  useUserForm,
  useUserQuery,
  useUserUpdateMutation,
} from '@opsopti/shared'
import type { CreateUserResponse, UpdateUserResponse } from '@opsopti/shared/src/services/api/api.types'
import { useAreFormQueriesFetched } from '@opsopti/shared/src/utils/tanstackHelpers'
import { observer } from 'mobx-react-lite'
import { createContext, Fragment, useContext, useEffect, useMemo } from 'react'
import type { z } from 'zod'

import { RadioCard } from '@/components/RadioCard'
import type { ScreenProps } from '@/components/Screen'
import { Screen } from '@/components/Screen'
import { SectionedForm, type SectionedFormProps } from '@/components/SectionedForm'
import type { RouteParams } from '@/configs/routes'
import {
  getFormScreenTitle,
  getFormSubmitLabel,
  showErrorNotification,
  showSuccessNotification,
} from '@/utils/componentCommons'
import { goBack } from '@/utils/navigation'

import { TableScreenDescription, tableScreenIcon } from './TableScreen'

type UserFormScreenProps = {
  params?: RouteParams['userNew'] | RouteParams['userEdit']
  type?: 'screen' | 'drawer'
  goBackFn?: () => void
  onAfterSubmitFn?: (group: CreateUserResponse | UpdateUserResponse) => void
}

type FormSection = {
  id: string | null
  type: 'screen' | 'drawer'
  form: UseFormReturnType<z.infer<UserSchema>>
  isMutating: boolean
  submitFn: () => Promise<void>
  goBackFn: () => void
}

const UserFormDataContext = createContext<FormSection>({} as FormSection)
const useUserFormData = () => useContext(UserFormDataContext)

export const UserFormScreen = observer(function UserFormScreen(props: UserFormScreenProps) {
  const { uiStore } = useGlobalState()

  const id = props.params?.id ?? null
  const isNew = useMemo(() => !id, [id])
  const type = props.type ?? 'screen'

  const { form } = useUserForm()

  const userQuery = useUserQuery({ params: { id } })
  const userCreateMutation = useUserCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })
  const userUpdateMutation = useUserUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  const areQueriesLoaded = useAreFormQueriesFetched([!isNew ? userQuery.isFetched : null], 200)

  useEffect(() => {
    if (areQueriesLoaded) {
      form.initialize(castUserToForm(userQuery.data))
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [areQueriesLoaded])

  useEffect(() => uiStore.hideLoadingOverlay, [])

  useHotkeys([['mod+S', submitFn]], [])

  const goBackFn = props.goBackFn ?? goBack

  async function submitFn() {
    const validations = form.validate()

    if (validations.hasErrors) {
      showErrorNotification({ title: 'Form Errors', message: 'Please fill out all required fields before submitting.' })
      return
    }

    let response: CreateUserResponse | UpdateUserResponse | undefined

    if (isNew) {
      response = await userCreateMutation.mutateAsync(form.getValues())
    } else {
      response = await userUpdateMutation.mutateAsync({ ...form.getValues(), id: id! })
    }

    props.onAfterSubmitFn?.(response)

    goBackFn()
  }

  const sections = useMemo(
    () =>
      [
        {
          id: 'general',
          title: 'General Details',
          subtitle: 'Basic details about the user.',
          collapsible: false,
          valid: isFieldsListValid(form, ['firstName', 'lastName', 'email', 'role', 'accountStatus']),
          ContentComponent: GeneralFields,
        },
        {
          id: 'additional',
          title: 'Additional Information',
          subtitle: 'Visibility settings, notes, and other information.',
          collapsible: false,
          valid: isFieldsListValid(form, ['visibilityStatus']),
          ContentComponent: AdditionalFields,
        },
        {
          id: 'actions',
          collapsible: false,
          ContentComponent: FormActions,
        },
      ] satisfies SectionedFormProps['sections'],
    [form],
  )

  const isMutating = userCreateMutation.isPending || userUpdateMutation.isPending
  const FormWrapper = type === 'screen' ? Screen : Fragment
  const FormWrapperProps: ScreenProps =
    type === 'screen'
      ? {
          maw: 1440,
          title: getFormScreenTitle(id, 'User', userQuery.data?.fullName ?? ''),
          icon: tableScreenIcon['users'],
          description: <TableScreenDescription entity="users" />,
        }
      : ({} as ScreenProps)

  return (
    <FormWrapper {...FormWrapperProps}>
      <UserFormDataContext.Provider value={{ id, form, submitFn, goBackFn, isMutating, type }}>
        <SectionedForm sections={sections} sectionsWithBorder={type === 'screen'} />
      </UserFormDataContext.Provider>
    </FormWrapper>
  )
})

function GeneralFields() {
  const props = useUserFormData()

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Switch
          {...props.form.getInputProps('accountStatus')}
          checked={props.form.getValues().accountStatus === 'enabled'}
          onChange={e => props.form.setFieldValue('accountStatus', e.currentTarget.checked ? 'enabled' : 'disabled')}
          labelPosition="right"
          onLabel="Yes"
          offLabel="No"
          label="Account Enabled?"
          description='When "off", the user will no longer be able to login into the system.'
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="Email"
          description="A unique email address for the user. This will be used for logging in."
          {...props.form.getInputProps('email')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="First Name"
          description="The user's first name."
          {...props.form.getInputProps('firstName')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="Last Name"
          description="The user's last name."
          {...props.form.getInputProps('lastName')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Select
          clearable
          label="Account Role"
          placeholder="Select a role..."
          description="The permissions level for access."
          data={
            [
              { value: 'member', label: 'Member' },
              { value: 'supervisor', label: 'Supervisor' },
              { value: 'admin', label: 'Admin' },
            ] satisfies Array<{ value: User['role'][number]; label: string }>
          }
          {...props.form.getInputProps('role')}
        />
      </Grid.Col>
    </Grid>
  )
}

function AdditionalFields() {
  const props = useUserFormData()

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group label="Visibility" withAsterisk {...props.form.getInputProps('visibilityStatus')}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={userSchema.shape.visibilityStatus.enum.internal}
              label="Internal"
              description="Users that are only visible to Supervisors and Admins."
            />

            <RadioComponent
              value={userSchema.shape.visibilityStatus.enum.public}
              label="Public"
              description="Users that are visible to all users."
            />

            <RadioComponent
              value={userSchema.shape.visibilityStatus.enum.archived}
              label="Archived"
              description="Archived users are hidden within the system. Note: This action does not prevent the user from logging in - you must disable the account to prevent access."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Supervisors"
          autosize
          minRows={4}
          description="Notes that can be used for internal purposes. Only visible to Supervisors."
          {...props.form.getInputProps('internalNotes')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Members"
          autosize
          minRows={4}
          description="Notes displayed publicly to Members."
          {...props.form.getInputProps('memberNotes')}
        />
      </Grid.Col>
    </Grid>
  )
}
function FormActions() {
  const props = useUserFormData()

  return (
    <Stack gap="xxl">
      <Group gap="xl">
        <Button size="lg" radius="sm" onClick={props.submitFn} loading={props.isMutating}>
          {getFormSubmitLabel(props.id, 'User')}
        </Button>

        {!props.isMutating && (
          <Button size="sm" radius="sm" variant="light" color="gray" onClick={props.goBackFn}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  )
}
